/**
 * Client-side session handling.
 *
 * Two modes, matching the backend (DESIGN.md §9.6):
 *
 * - **Cognito**: a bearer access token, held in memory only. Deliberately not in
 *   `localStorage` — a token at rest in a browser store is readable by any script that gets
 *   injected, and this application displays PHI.
 * - **Dev**: an `X-Dev-User` header naming a seeded subject, so the portal can be worked on
 *   before a pool exists. The backend refuses this unless it is running with both
 *   `AUTH_MODE=dev` and `APP_ENV=local`, so switching it on here is not sufficient to bypass
 *   anything in a deployed environment.
 *
 * Nothing here asserts identity. The subject or token selects *who is asking*; roles,
 * tenancy and permissions come back from the server via `GET /whoami`.
 */

const DEV_SUBJECT_KEY = 'scribe.devSubject';

let accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

/**
 * The dev subject is persisted so a page reload does not lose it. It is not a credential —
 * it is a username the backend only honours in local mode — so storing it is not the risk
 * that storing a token would be.
 */
export function getDevSubject(): string | null {
  try {
    return window.localStorage.getItem(DEV_SUBJECT_KEY);
  } catch {
    return null;
  }
}

export function setDevSubject(subject: string | null): void {
  try {
    if (subject) window.localStorage.setItem(DEV_SUBJECT_KEY, subject);
    else window.localStorage.removeItem(DEV_SUBJECT_KEY);
  } catch {
    /* private browsing — the header simply will not be sent */
  }
}

export function authHeaders(): Record<string, string> {
  if (accessToken) return { Authorization: `Bearer ${accessToken}` };
  const subject = getDevSubject();
  return subject ? { 'X-Dev-User': subject } : {};
}

/**
 * The API answered, but has no healthy backend behind it — in dev, typically the scheduled
 * scale-down outside working hours. Distinct from a network failure and from an auth failure: a 503
 * used to surface as "Could not reach the server to establish identity" on the sign-in screen, which
 * read like an authentication problem (2026-09-14).
 */
export class ServiceUnavailableError extends Error {
  constructor(message = 'The Scribe service is not running right now.') {
    super(message);
    this.name = 'ServiceUnavailableError';
  }
}

/** Raised on 401/403 so callers can distinguish "sign in" from "you may not do that". */
export class AuthError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'AuthError';
  }
}

/**
 * The request never got a response at all — a connection that timed out or was reset, as opposed
 * to a server that answered with an error status. Distinct from `ServiceUnavailableError` (a 503:
 * the server answered, just with nothing behind it) and from a plain `AuthError`: both of those
 * used to arrive as a bare `TypeError: Failed to fetch` from the browser, which read as an
 * authentication problem on the sign-in screen and gave no hint that the *network path*, not the
 * server, was the thing to check (2026-09-15 — a request that hung past a minute with no visible
 * failure, and others that failed with no status code at all, both traced to `authorizedFetch`
 * having no timeout: a stalled local connection or dev proxy hop hung forever instead of failing
 * fast). `authorizedFetch` retries once before raising this, so it means the retry failed too.
 */
export class NetworkError extends Error {
  constructor(message = 'Could not reach the server. Check your connection and try again.') {
    super(message);
    this.name = 'NetworkError';
  }
}
